import { useState, useRef, useEffect } from "react";
import {
    BookOpen, Building, Hammer, Recycle, Factory, Scissors, Briefcase,
    Calculator, FileText, Wallet, Users as UsersIcon, ShieldCheck,
    PlayCircle, ChevronRight, FileSpreadsheet, Truck, ClipboardList, Package, Cog,
} from "lucide-react";
import { PageHeader, Card } from "@/components/common/UI";

/* ============================================================
 * USER MANUAL: Inbuilt help for new CostIQ users
 * Read-only documentation. Does not change any business logic.
 * ============================================================ */

const SECTIONS = [
    { id: "intro",      icon: PlayCircle,  title: "Getting Started" },
    { id: "plants",     icon: Building,    title: "1 · Set Up Plants" },
    { id: "grades",     icon: Hammer,      title: "2 · Steel Grades" },
    { id: "scrap",      icon: Recycle,     title: "3 · Scrap Rates" },
    { id: "machines",   icon: Factory,     title: "4 · Machines & Machine Rates" },
    { id: "process",    icon: Scissors,    title: "5 · Process Rates (Cut / Weld)" },
    { id: "conversion-operations", icon: Cog, title: "5B · Conversion Operations" },
    { id: "customers",  icon: Briefcase,   title: "6 · Customers & Norms" },
    { id: "suppliers",  icon: Truck,       title: "7 · Suppliers & Steel Rates" },
    { id: "bop-catalogue", icon: Package,  title: "7B · BOP Catalogue" },
    { id: "zbc",        icon: Calculator,  title: "8 · Build a ZBC (Should-Cost)" },
    { id: "ai-zbc",     icon: Calculator,  title: "9 · Intelligent-Assisted ZBC (Drawings)" },
    { id: "supplier-compare", icon: FileSpreadsheet, title: "10 · Supplier Workbook Compare" },
    { id: "rfq",        icon: FileText,    title: "11 · Create an RFQ" },
    { id: "approval",   icon: ShieldCheck, title: "12 · Approval Workflow" },
    { id: "settlement", icon: Wallet,      title: "13 · Settlement (Quarterly Steel Revision)" },
    { id: "reports",    icon: ClipboardList, title: "14 · Reports" },
    { id: "roles",      icon: UsersIcon,   title: "15 · Roles & Permissions" },
    { id: "tips",       icon: BookOpen,    title: "16 · Tips & Best Practices" },
];

function Step({ n, children }) {
    return (
        <div className="flex gap-3 mb-3">
            <div className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--copper))] text-white text-[12px] font-semibold flex items-center justify-center font-mono">
                {n}
            </div>
            <div className="text-[13px] text-slate-700 leading-relaxed pt-1">{children}</div>
        </div>
    );
}

function Snippet({ children, label }) {
    return (
        <div className="my-3 rounded-md border border-slate-200 bg-slate-50/70 overflow-hidden">
            {label && <div className="eyebrow text-slate-500 px-3 pt-2.5 pb-1">{label}</div>}
            <pre className="text-[11.5px] font-mono text-slate-700 px-3 py-2 overflow-x-auto whitespace-pre-wrap leading-relaxed">{children}</pre>
        </div>
    );
}

function Callout({ kind = "info", title, children }) {
    const palette = {
        info:    "border-sky-200 bg-sky-50/60 text-sky-900",
        warning: "border-amber-200 bg-amber-50/60 text-amber-900",
        success: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
    }[kind];
    return (
        <div className={`my-3 rounded-md border ${palette} p-3 text-[12.5px] leading-relaxed`}>
            {title && <div className="font-semibold mb-1">{title}</div>}
            {children}
        </div>
    );
}

function FieldList({ items }) {
    return (
        <div className="my-3 rounded-md border border-slate-200 overflow-hidden">
            <table className="w-full text-[12px]">
                <thead>
                    <tr className="bg-slate-50/60 border-b border-slate-200">
                        <th className="text-left py-2 px-3 eyebrow text-slate-500 w-44">Field</th>
                        <th className="text-left py-2 px-3 eyebrow text-slate-500">What to enter & why it matters</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(([field, desc]) => (
                        <tr key={field} className="border-b border-slate-100 last:border-0">
                            <td className="py-2 px-3 font-medium text-slate-800">{field}</td>
                            <td className="py-2 px-3 text-slate-600">{desc}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ---------- Enhanced learning helpers ---------- */

function Screenshot({ src, caption, alt }) {
    return (
        <figure className="my-4 rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
            <img
                src={src}
                alt={alt || caption || "CostIQ screenshot"}
                className="w-full h-auto block"
                loading="lazy"
            />
            {caption && (
                <figcaption className="text-[11.5px] text-slate-500 border-t border-slate-200 px-3 py-2 bg-slate-50/60 italic">
                    {caption}
                </figcaption>
            )}
        </figure>
    );
}

function WhyMatters({ children }) {
    return (
        <div className="my-3 rounded-md border-l-4 border-[hsl(var(--copper))] bg-[hsl(var(--copper-soft))]/40 pl-3 pr-3 py-2.5">
            <div className="eyebrow text-[hsl(var(--copper))] mb-0.5">WHY THIS MATTERS</div>
            <div className="text-[12.5px] text-slate-700 leading-relaxed">{children}</div>
        </div>
    );
}

function IfSkipped({ children }) {
    return (
        <div className="my-3 rounded-md border-l-4 border-rose-400 bg-rose-50/50 pl-3 pr-3 py-2.5">
            <div className="eyebrow text-rose-700 mb-0.5">WHAT BREAKS IF YOU SKIP THIS</div>
            <div className="text-[12.5px] text-rose-900 leading-relaxed">{children}</div>
        </div>
    );
}

function FlowsInto({ children }) {
    return (
        <div className="my-3 rounded-md border-l-4 border-emerald-400 bg-emerald-50/50 pl-3 pr-3 py-2.5">
            <div className="eyebrow text-emerald-700 mb-0.5">HOW IT FLOWS DOWNSTREAM</div>
            <div className="text-[12.5px] text-emerald-900 leading-relaxed">{children}</div>
        </div>
    );
}

function Example({ title, inputs, output }) {
    return (
        <div className="my-4 rounded-lg border border-slate-300 overflow-hidden">
            <div className="bg-slate-800 text-slate-100 px-3 py-2 text-[11.5px] font-semibold tracking-wide uppercase">
                {title || "Worked Example"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 bg-white">
                <div className="p-3">
                    <div className="eyebrow text-slate-500 mb-1.5">Inputs</div>
                    <table className="w-full text-[11.5px]">
                        <tbody>
                            {inputs.map(([k, v]) => (
                                <tr key={k}>
                                    <td className="py-0.5 pr-2 text-slate-500 whitespace-nowrap">{k}</td>
                                    <td className="py-0.5 font-mono text-slate-800 text-right">{v}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="p-3 bg-emerald-50/40">
                    <div className="eyebrow text-emerald-700 mb-1.5">Result</div>
                    <table className="w-full text-[11.5px]">
                        <tbody>
                            {output.map(([k, v]) => (
                                <tr key={k}>
                                    <td className="py-0.5 pr-2 text-emerald-800 whitespace-nowrap">{k}</td>
                                    <td className="py-0.5 font-mono text-emerald-900 text-right font-semibold">{v}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function PipelineDiagram() {
    const nodes = [
        { label: "Plants",         hint: "12 units" },
        { label: "Steel Grades",   hint: "28 grades × ₹/kg" },
        { label: "Scrap Rates",    hint: "recovery credit" },
        { label: "Machines",       hint: "resources" },
        { label: "Machine Rates",  hint: "₹/hr burden" },
        { label: "Process Rates",  hint: "cut / weld" },
        { label: "Customers",      hint: "OEMs + norms" },
        { label: "Suppliers",      hint: "vendor steel rates" },
    ];
    return (
        <div className="my-4 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
            <div className="eyebrow text-slate-500 mb-3">FOUNDATION → COST PIPELINE</div>
            <div className="flex flex-wrap items-center gap-1.5">
                {nodes.map((n, i) => (
                    <div key={n.label} className="flex items-center gap-1.5">
                        <div className="rounded-md border border-[hsl(var(--copper-soft))] bg-white px-2.5 py-1.5 min-w-[92px] text-center shadow-sm">
                            <div className="text-[11.5px] font-semibold text-slate-800">{n.label}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{n.hint}</div>
                        </div>
                        {i < nodes.length - 1 && (
                            <ChevronRight className="w-3.5 h-3.5 text-[hsl(var(--copper))] shrink-0" />
                        )}
                    </div>
                ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5 pl-2">
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 rotate-90" />
                <div className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-800">ZBC (Should-Cost)</div>
                <ChevronRight className="w-3.5 h-3.5 text-emerald-500" />
                <div className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-800">RFQ (Customer Price)</div>
                <ChevronRight className="w-3.5 h-3.5 text-emerald-500" />
                <div className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-800">Approval</div>
                <ChevronRight className="w-3.5 h-3.5 text-emerald-500" />
                <div className="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-800">Settlement</div>
            </div>
        </div>
    );
}

function Section({ id, title, icon: Icon, children }) {
    return (
        <Card id={id} className="p-6 scroll-mt-24" data-testid={`manual-section-${id}`}>
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-md bg-[hsl(var(--copper-soft))] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[hsl(var(--copper))]" />
                </div>
                <h2 className="font-display text-xl font-semibold">{title}</h2>
            </div>
            <div className="prose-sm max-w-none text-[13px] text-slate-700 leading-relaxed">
                {children}
            </div>
        </Card>
    );
}

export default function UserManual() {
    const [active, setActive] = useState("intro");
    const observerRef = useRef(null);

    useEffect(() => {
        const opts = { rootMargin: "-30% 0px -60% 0px", threshold: 0 };
        const obs = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) setActive(e.target.id);
            });
        }, opts);
        SECTIONS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) obs.observe(el);
        });
        observerRef.current = obs;
        return () => obs.disconnect();
    }, []);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div data-testid="user-manual-page">
            <PageHeader
                title="User Manual"
                subtitle="A step-by-step guide to using CostIQ, from setting up your foundation data to closing a quarterly steel-price settlement."
            />

            <div className="grid grid-cols-12 gap-6 mt-6">
                {/* ====== Left: Table of Contents ====== */}
                <aside className="col-span-12 lg:col-span-3">
                    <Card className="p-3 sticky top-20 max-h-[calc(100vh-7rem)] overflow-auto">
                        <div className="eyebrow text-slate-500 px-2 py-2">CONTENTS</div>
                        <nav className="space-y-0.5">
                            {SECTIONS.map((s) => {
                                const Ic = s.icon;
                                const isActive = active === s.id;
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => scrollTo(s.id)}
                                        data-testid={`manual-toc-${s.id}`}
                                        className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] transition-colors ${
                                            isActive
                                                ? "bg-[hsl(var(--copper-soft))] text-[hsl(var(--copper))] font-medium"
                                                : "text-slate-600 hover:bg-slate-100"
                                        }`}
                                    >
                                        <Ic className="w-3.5 h-3.5 shrink-0" />
                                        <span className="truncate">{s.title}</span>
                                        {isActive && <ChevronRight className="w-3 h-3 ml-auto" />}
                                    </button>
                                );
                            })}
                        </nav>
                    </Card>
                </aside>

                {/* ====== Right: Manual Content ====== */}
                <main className="col-span-12 lg:col-span-9 space-y-6">

                    {/* ========== 0 · GETTING STARTED ========== */}
                    <Section id="intro" title="Getting Started" icon={PlayCircle}>
                        <p>
                            Welcome to <strong>CostIQ</strong>, ABC COMPANY's single source of truth for
                            pricing, costing, and customer negotiations. This manual walks through every
                            workflow in the order you'll use them.
                        </p>

                        <Callout title="What CostIQ replaces" kind="info">
                            CostIQ replaces the disconnected Excel files that used to live on personal laptops:
                            ZBC sheets, RFQ price files, and quarterly settlement tracker. Everything is now in
                            one place, with audit trails and role-based approvals.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Recommended reading order</h3>
                        <p>
                            If you're a new user, follow these chapters in sequence. Foundation data (1-6) must
                            be set up <em>before</em> you can create a ZBC, and a ZBC must exist before it can
                            feed an RFQ.
                        </p>
                        <Snippet label="WORKFLOW PIPELINE">
{`Plants → Steel Grades → Scrap → Machines → Process Rates → Customers & Norms → Suppliers
   │
   └─► ZBC (Should-Cost) ─► RFQ (Customer Price) ─► Approval ─► Settlement ─► Reports`}
                        </Snippet>

                        <PipelineDiagram />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">How to log in</h3>
                        <Step n={1}>Open the CostIQ URL sent to you and land on the login screen.</Step>
                        <Step n={2}>Enter your <strong>work email</strong> and <strong>password</strong> (a one-time password is emailed on account creation; change it on first sign-in).</Step>
                        <Step n={3}>Click <strong>Sign in</strong>. You land on the Dashboard which shows this quarter's KPIs, alerts, and quick actions.</Step>
                        <Screenshot
                            src="/manual/01-login.png"
                            caption="Sign-in screen. Use the email your Corporate Admin registered you with (typically firstname.lastname@testemail.com)."
                        />
                        <Screenshot
                            src="/manual/02-dashboard.png"
                            caption="Dashboard: ZBCs this month, RFQs in progress, Settlements this quarter, plus a live steel-rate monitor and one-click 'New ZBC / New RFQ / New Settlement' actions."
                        />

                        <Callout kind="info" title="Anomaly Detector">
                            Corporate Admin, Finance Pricing, Dept Head, Director, and Purchase Manager
                            dashboards also show an <strong>Anomaly Detector</strong> card when it finds
                            something worth a look: a VERIFIED ZBC whose cost-per-kg sits well outside the
                            normal spread for its material category, or a supplier quote that came in below
                            should-cost. Both signals are plain statistics, an IQR outlier check and a
                            variance-badge check, not AI. Clicking a row opens that ZBC. The card stays
                            hidden when there's nothing flagged, so it's never noise on a clean day.
                        </Callout>

                        <Callout kind="warning" title="A note on roles">
                            Most setup screens are visible only to <strong>Corporate Admin</strong>. If you don't
                            see a menu item from this manual, your role does not have access. See <a className="underline" href="#roles" onClick={(e) => { e.preventDefault(); scrollTo("roles"); }}>Roles &amp; Permissions</a>.
                        </Callout>

                        <Callout kind="success" title="Pre-loaded India market data (Feb 2026)">
                            CostIQ ships with realistic Indian market reference data so you can start creating
                            ZBCs immediately: <strong>15 plants</strong> across Pune / Bengaluru / Chennai / Jamshedpur /
                            Pantnagar / Ahmedabad / Indore, <strong>28 steel grades</strong> (IS&nbsp;2062, IS&nbsp;513,
                            Domex, Hardox, SAILMA, EN&nbsp;10025 etc.) priced at Feb 2026 INR/kg,
                            <strong>22 machines</strong> (Trumpf, Bystronic, Amada, ABB, Kuka…) with
                            <strong> APPROVED machine rates</strong>, <strong>24 process rates</strong> for
                            cutting &amp; welding, <strong>10 OEM customers</strong>
                            (Tata Motors, Mahindra, Ashok Leyland, VECV, JCB, Kobelco, Komatsu, TAFE, Force Motors,
                            Volvo Export) with their formula variants pre-configured, and <strong>4 suppliers</strong>{" "}
                            with their own steel rates. Edit any row to match your own procurement rates.
                        </Callout>
                    </Section>

                    {/* ========== 1 · PLANTS ========== */}
                    <Section id="plants" title="1 · Set Up Plants" icon={Building}>
                        <p>
                            A <strong>Plant</strong> is one of ABC COMPANY's manufacturing locations. Every ZBC and
                            RFQ is anchored to a plant so capacity and machine-rate calculations land on the
                            right cost centre.
                        </p>

                        <WhyMatters>
                            Plants are the <em>root</em> of the foundation tree. A machine belongs to a plant,
                            a machine has a rate, and a ZBC picks a plant so that only that plant's machines
                            (and their hourly rates) can be used to price the part. Get the plants right first
                            and everything below inherits the correct cost baseline.
                        </WhyMatters>

                        <IfSkipped>
                            The ZBC form can't offer any machines in the Conversion Cost table because there's
                            nothing to attach them to. The RFQ engine will refuse to save with the error
                            "plant_id is required".
                        </IfSkipped>

                        <Step n={1}>Go to <strong>Foundation Setup → Plants</strong> in the left menu.</Step>
                        <Step n={2}>Click <strong>+ Add Plant</strong> (top right).</Step>
                        <Step n={3}>Fill in the form (only Corporate Admin can do this):</Step>

                        <FieldList items={[
                            ["Plant Code",      "Short unique code, e.g. PUNE-01, BLR-01. This code appears in every downstream export (ZBC PDF, RFQ Excel), so use something short and easy to eyeball."],
                            ["Plant Name",      "Full descriptive name shown in dropdowns and PDFs, e.g. 'Pune Unit 1, Chakan'. Include the location cue so users pick the right one in the ZBC form."],
                            ["City / State",    "Physical location. Used on PDF letterheads and the RFQ commercial cover page. Also drives the freight zone hint in future modules."],
                            ["Business Unit",   "Which BU this plant belongs to. Currently placeholder test data (a 6/6 split across the 12 active plants) pending the real BU/plant structure from ABC COMPANY, rates and norms are not yet configurable per-BU."],
                            ["Phase / Status",  "ACTIVE for live plants; INACTIVE for future or shut sites. INACTIVE plants are hidden from ZBC/RFQ dropdowns but remain visible in historical audit trails."],
                        ]} />

                        <Screenshot
                            src="/manual/03-plants.png"
                            caption="Plants list. 12 active plants across Pune, Bengaluru, Chennai, Jamshedpur, Pantnagar, Ahmedabad and Indore, plus 3 Phase-3 sites marked INACTIVE."
                        />

                        <Example
                            title="Example: Adding a new Pune plant"
                            inputs={[
                                ["Plant Code", "PUNE-05"],
                                ["Plant Name", "Pune Unit 5, Talawade"],
                                ["City", "Pune"],
                                ["State", "Maharashtra"],
                                ["Status", "ACTIVE"],
                            ]}
                            output={[
                                ["Available in ZBC?", "Yes"],
                                ["Machines that can be tagged", "0 (add next)"],
                                ["Appears on Dashboard as", "Live plant"],
                            ]}
                        />

                        <FlowsInto>
                            Every <strong>Machine</strong> you create later must pick one of these plants.
                            The <strong>ZBC form</strong> filters the machine list by the selected plant, so a
                            ZBC for PUNE-01 will only see PUNE-01 machines and their approved rates.
                        </FlowsInto>
                    </Section>

                    {/* ========== 2 · STEEL GRADES ========== */}
                    <Section id="grades" title="2 · Steel Grades & Scrap" icon={Hammer}>
                        <p>
                            Steel Grades are the raw-material catalogue. Each grade carries a <strong>rate per
                            kg</strong> that drives the Material Cost section in every ZBC.
                        </p>

                        <WhyMatters>
                            Material typically accounts for <strong>55-75%</strong> of a fabricated part's total
                            cost. A ₹2/kg error on IS 2062 E250 sheet, spread across a 12 kg blank,
                            distorts your should-cost by ₹24 per piece, and by lakhs across a monthly volume
                            of 5,000 units. This table is the single point where those rates get refreshed
                            each quarter. Skip it and the ZBC's "Calculate from weight × grade rate" mode just
                            shows "No grades configured," so every rupee has to be entered by hand.
                        </WhyMatters>

                        <Step n={1}>Go to <strong>Foundation Setup → Steel Grades &amp; Scrap</strong>.</Step>
                        <Step n={2}>Click <strong>+ Add Grade</strong>.</Step>
                        <Step n={3}>Fill in the form fields:</Step>

                        <FieldList items={[
                            ["Grade Code",        "Unique short code, e.g. IS2062-E250-A, DOMEX-650, HARDOX-400. Appears in ZBC/RFQ exports so keep it identical to your ERP master."],
                            ["Grade Name",        "Descriptive label shown in the ZBC dropdown, e.g. 'IS 2062 E250 Grade A'. Longer form makes it obvious what the operator is picking."],
                            ["Specification",     "Standard / spec sheet reference, e.g. 'Hot rolled structural steel, YS 250 MPa'. Used on the audit trail so anyone can trace which spec was in force when a ZBC was created."],
                            ["Rate (₹/kg)",       "Current landed purchase rate (mill cost + duties + freight). This is what drives the Material Cost line in every new ZBC that picks this grade."],
                            ["Thickness Min/Max", "Thickness bracket (mm) for which this grade applies, e.g. 0-12 mm. When a ZBC blank thickness is entered, CostIQ auto-restricts the dropdown to matching brackets."],
                            ["Effective Date",    "Date this rate became valid. The Steel Price History module reads this to trigger quarterly Settlement calculations."],
                            ["Customer Tag",      "Blank = usable for all customers. Set to a customer code (e.g. VOLVO-EXPORT) to route to a customer-specific grade / rate."],
                            ["Status",            "APPROVED makes it usable in ZBCs; DRAFT hides it from the form until Finance signs off on the rate."],
                        ]} />

                        <Screenshot
                            src="/manual/04-steel-grades.png"
                            caption="Steel Grades master: 28 Feb-2026 India-market grades pre-loaded (IS 2062, IS 513 CR, Domex, Hardox, SAILMA, EN 10025). The 'Update Rate' button opens a history-tracked rate change dialog."
                        />

                        <Callout kind="info" title="Customer-tagged grades">
                            If a customer like Volvo demands their own steel grade (and rate), tag the grade
                            with the customer name. CostIQ will auto-pick that grade for ZBCs created against
                            that customer.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Updating a rate (the audit-safe way)</h3>
                        <p>
                            Click the <strong>Update Rate</strong> button on any grade row. Enter the new rate
                            and a reason (e.g. "Q3-2026 SAIL revision"). CostIQ:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 text-[12.5px] my-2">
                            <li>archives the old rate + your user id + timestamp in <em>Steel Price History</em>,</li>
                            <li>counts how many existing ZBCs use this grade and shows the impact,</li>
                            <li>flags those ZBCs with a <em>recalc-pending</em> badge so Finance can decide whether to refresh them,</li>
                            <li>never silently mutates a historical ZBC: the snapshot is preserved.</li>
                        </ul>

                        <Example
                            title="Example:Rate update cascade"
                            inputs={[
                                ["Grade", "IS2062-E250-A"],
                                ["Old rate", "₹62.80 /kg"],
                                ["New rate", "₹65.10 /kg"],
                                ["ZBCs using this grade", "14"],
                                ["Reason", "Q3 SAIL revision"],
                            ]}
                            output={[
                                ["Historical ZBCs", "Unchanged (₹62.80 snap)"],
                                ["Flagged as recalc-pending", "14 ZBCs"],
                                ["New ZBCs from today", "Use ₹65.10 /kg"],
                                ["Audit entry", "Written to Steel Price History"],
                            ]}
                        />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Rate Intelligence</h3>
                        <p>
                            The <strong>Rate Intelligence</strong> panel at the top of this page reads each
                            grade's own recorded rate-change history and shows the % change over a lookback
                            window (30 / 90 / 180 / 365 days), whether it's Rising, Falling, or Stable, and how
                            many times the rate moved in that window. It is a plain read of what already
                            happened, not a forecast, and it never changes a rate on its own.
                        </p>

                        <Callout kind="info" title="Explain top movers is optional and separate from the numbers">
                            The <strong>Explain top movers</strong> button asks the Intelligent Advisor for a
                            one-sentence note on the biggest Rising/Falling grades, strictly on top of the numbers
                            already shown, never replacing them. If no API key is configured, or the call fails
                            or times out, the button simply produces no note; the trend numbers themselves are
                            always computed the same deterministic way either way.
                        </Callout>

                        <FlowsInto>
                            Every new <strong>ZBC → Material Cost</strong> line looks up the current rate from
                            this table. The <strong>Settlement</strong> module reads the rate history to
                            compute Direct Material Cost (DMC) deltas each quarter. The same kind of statistical
                            (not AI) reasoning also drives the Dashboard's <strong>Anomaly Detector</strong> card;
                            see the note at the end of Getting Started.
                        </FlowsInto>
                    </Section>

                    {/* ========== 3 · SCRAP ========== */}
                    <Section id="scrap" title="3 · Scrap Rates" icon={Recycle}>
                        <p>
                            Scrap Rates record what ABC COMPANY gets back for selling cutting offcuts and rejects to
                            scrap dealers. This rate drives the <strong>Scrap Credit</strong> deduction in every
                            ZBC's material section (96% recoverable scrap × scrap rate). Understating it makes a
                            ZBC look more expensive than reality and hands the supplier a negotiation lever; on a
                            heavy fabricated part with 8-12% scrap, this line alone can swing net material by
                            ₹80-150 per piece. Leave a scrap rate unset and the ZBC's Scrap Credit line simply
                            reads ₹0, overstating Net Material Cost.
                        </p>

                        <Step n={1}>Go to <strong>Foundation Setup → Scrap Rates</strong>.</Step>
                        <Step n={2}>Click <strong>+ Add Scrap Rate</strong>.</Step>
                        <Step n={3}>Enter the fields:</Step>

                        <FieldList items={[
                            ["Scrap Code",     "Short code, e.g. MS-HEAVY, CR-SCRAP, TURNINGS. Tie this to your scrap-dealer contract codes so month-end reconciliation stays clean."],
                            ["Description",    "Type of scrap (e.g. 'MS Heavy Melting Scrap'). Shows in the ZBC scrap-type dropdown."],
                            ["Rate (₹/kg)",    "Current sale rate to the scrap dealer. Feb-2026 India market band: ₹28-46 /kg depending on type."],
                            ["Effective Date", "Date this rate became valid. Referenced by the audit log when a ZBC is recomputed later."],
                            ["Status",         "ACTIVE for live rates; INACTIVE for retired scrap types."],
                        ]} />

                        <Screenshot
                            src="/manual/13-scrap-rates.png"
                            caption="Scrap Rates master: 5 material scrap types (MS Heavy, MS Light, CR, Hardox, Turnings) plus 4 ZBC-allowance percentages (Recoverable %, Irrecoverable %, Plasma / Laser burning loss)."
                        />

                        <Example
                            title="Example:Scrap credit on a 12 kg blank"
                            inputs={[
                                ["Grade", "IS2062-E250-A @ ₹62.80/kg"],
                                ["Blank weight (gross)", "12.00 kg"],
                                ["Net weight after cutting", "10.55 kg"],
                                ["Recoverable scrap", "4% of gross = 0.48 kg"],
                                ["Scrap rate (MS-HEAVY)", "₹42.80 /kg"],
                            ]}
                            output={[
                                ["Gross material", "12.00 × 62.80 = ₹753.60"],
                                ["Scrap credit", "0.48 × 42.80 = ₹20.54"],
                                ["Net material cost", "₹733.06"],
                            ]}
                        />
                    </Section>

                    {/* ========== 4 · MACHINES ========== */}
                    <Section id="machines" title="4 · Machines & Machine Rates" icon={Factory}>
                        <p>
                            Machines are the production resources at each plant. The <strong>Machine Rate</strong>
                            (₹ per hour) is the hourly burden cost used for the Conversion Cost section in ZBC.
                            Together they answer: <em>"How much does one hour of this asset cost the company?"</em>{" "}
                            Conversion cost is typically the second-largest line on a fabricated part (15-30% of
                            net), and the Machine Rate is a composite of power, operator, maintenance,
                            depreciation, and overhead. Get it right once and every future ZBC prices time
                            correctly.
                        </p>

                        <h3 className="font-display text-base font-semibold mt-4 mb-2">Step A: Add the physical asset</h3>
                        <Step n={1}>Foundation Setup → <strong>Machines</strong>.</Step>
                        <Step n={2}>Click <strong>+ Add Machine</strong> and pick the plant + type.</Step>

                        <FieldList items={[
                            ["Machine Code",     "Short unique identifier, e.g. LASER-01, PRESS-03. Kept short so it fits in the ZBC conversion-cost operation dropdown."],
                            ["Machine Name",     "Full descriptive name: the make and model, e.g. 'Trumpf TruLaser 5030'. This is what a costing engineer sees when picking the operation."],
                            ["Plant",            "Which plant this machine belongs to. A ZBC created for PUNE-01 will only see PUNE-01 machines."],
                            ["Type",             "LASER, CNC_PRESS_BRAKE, ROBOT_WELDER, MIG_WELDER, DRILL, BANDSAW, MECH_PRESS, HYD_PRESS, PAINT_LINE. Used by the Intelligent ZBC route inference."],
                            ["Capacity per hour","Pieces/hr for high-volume presses, sq-m/hr for lasers, or 'jobs per shift' as an operator planning hint. Not used in cost math, but drives the Foundation-Gap KPI on the Dashboard."],
                            ["Shift Pattern",     "SINGLE_SHIFT or TWO_SHIFT: how many shifts this machine actually runs per day."],
                            ["Shift Hours/Day",   "Hours per shift, e.g. 8. Combined with Shift Pattern, CostIQ shows an Effective Daily Capacity (Capacity per hour × shift hours × 1 or 2 shifts) on the Machines list, as a planning reference."],
                            ["Status",           "ACTIVE keeps it in ZBC dropdowns; INACTIVE hides it (e.g. machine sold or under overhaul)."],
                        ]} />

                        <Callout kind="warning" title="Shift configuration is informational only">
                            Effective Daily Capacity is shown for capacity planning, e.g. spotting that a
                            machine running two shifts can absorb more monthly volume than the ZBC's assumed
                            single-shift figure. It never changes an <strong>APPROVED</strong> Machine Rate's
                            ₹/hr on its own; a rate still only ever changes through the Machine Rates approval
                            workflow in Step B below.
                        </Callout>

                        <Screenshot
                            src="/manual/05-machines.png"
                            caption="Machines master. Each row shows the machine, its plant, and current status. Click 'Edit' to update: the change is tracked in the audit log."
                        />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step B: Set the hourly rate (approval-gated)</h3>
                        <p>
                            Go to <strong>Foundation Setup → Machine Rates</strong>. Machine Rates are the
                            <em> only</em> foundation table with an approval workflow: a Plant Manager submits,
                            a Corporate Admin approves.
                        </p>

                        <FieldList items={[
                            ["Machine",              "Pick the asset. The plant is inherited from the machine record."],
                            ["Power (kW)",           "Connected load: used to compute the electricity component."],
                            ["Power rate (₹/kWh)",   "Landed grid tariff (Feb-2026 Maharashtra industrial ~₹9.50-9.80/kWh, Karnataka ~₹9.40)."],
                            ["Operator cost (₹/hr)", "Loaded operator wage including PF/ESI/leave loading (Feb-2026 skilled operator ~₹185-235/hr)."],
                            ["Maintenance (₹/hr)",   "AMC + spares amortised on planned hours."],
                            ["Depreciation (₹/hr)",  "Asset cost ÷ useful life ÷ planned hours: driven by Finance policy."],
                            ["Overhead (₹/hr)",      "Rent, indirect labour, utilities, tooling share, EHS."],
                        ]} />

                        <Callout kind="info" title="Formula (auto-calculated as you type)">
                            <span className="font-mono text-[12px]">Calculated Rate = power_kw × power_rate + operator + maintenance + depreciation + overhead</span>
                        </Callout>

                        <Screenshot
                            src="/manual/06-machine-rates.png"
                            caption="Machine Rates: one APPROVED rate per machine. The 'Calculated' column reflects the composite ₹/hr. Rates ship pre-approved for the 22 seeded machines (₹450 - ₹2,553/hr)."
                        />

                        <Example
                            title="Example:Trumpf TruLaser 5030 hourly rate"
                            inputs={[
                                ["Power", "6.0 kW"],
                                ["Grid tariff (Maharashtra)", "₹9.80 /kWh"],
                                ["Operator", "₹195 /hr"],
                                ["Maintenance", "₹145 /hr"],
                                ["Depreciation", "₹480 /hr"],
                                ["Overhead", "₹220 /hr"],
                            ]}
                            output={[
                                ["Power component", "6.0 × 9.80 = ₹58.80"],
                                ["Composite rate", "₹58.80 + 195 + 145 + 480 + 220 = ₹1,098.80 /hr"],
                                ["Used in ZBC as", "LASER cycle-time cost line"],
                            ]}
                        />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Approval workflow</h3>
                        <Snippet label="MACHINE RATE STATES">
{`DRAFT ─────► SUBMITTED ─────► APPROVED   (usable in ZBC)
                          │
                          └──► REJECTED   (Plant Manager fixes and re-submits)`}
                        </Snippet>
                        <p className="text-[12.5px] text-slate-600">
                            Only <strong>APPROVED</strong> rates surface in the ZBC form. When a Plant Manager
                            submits a new rate, existing ZBCs keep using their snapshotted rate until explicitly
                            recomputed, so nothing drifts silently. Every ZBC Conversion Cost row auto-fills and
                            locks its ₹/hr from this table when you pick the machine (only the time/quantity
                            stays manual; pick "(manual)" instead of a machine if it isn't in this table yet),
                            and the Intelligent-Assisted ZBC uses the same rates for its cycle-time inference.
                        </p>
                    </Section>

                    {/* ========== 5 · PROCESS RATES ========== */}
                    <Section id="process" title="5 · Process Rates (Cutting & Welding)" icon={Scissors}>
                        <p>
                            Process Rates are the rate tables for <strong>Laser / Plasma Cutting</strong>
                            (₹/mtr/mm) and <strong>Welding</strong> (₹/mtr for each fillet size). These are
                            looked up automatically when you enter cut perimeter or weld joints in a ZBC.
                            Cutting and welding are usage-based costs, not machine-hour costs: charging "₹/mtr"
                            gives Sales a defensible RFQ line-item that customers understand, instead of burying
                            these costs in overhead. Leave a rate unset and the matching ZBC field, cut
                            perimeter or a weld line, simply holds zero, understating should-cost and losing margin
                            without anyone noticing.
                        </p>

                        <Step n={1}>Foundation Setup → <strong>Process Rates</strong>.</Step>
                        <Step n={2}>Switch tabs: <em>Cutting</em> for thickness brackets, <em>Welding</em> for fillet brackets.</Step>
                        <Step n={3}>Click <strong>+ Add Rate</strong>:</Step>

                        <FieldList items={[
                            ["Process Type",       "CUTTING (thickness-based), WELDING (fillet-based), ARGON_CO2_WELD (Volvo Argon-CO2 shielding gas), FLAT_RATE_WELD (Tata settled ₹/m), LASER_CUTTING, SHEARING."],
                            ["Thickness / Fillet", "Bracket size in mm. For cutting this is the plate thickness; for welding it is the fillet leg length."],
                            ["Rate",               "₹ per mtr per mm (cutting) or ₹ per mtr (welding). Set the highest realistic rate: CostIQ never rounds DOWN."],
                            ["Customer Tag",       "Optional. Blank = usable for all customers. Set to route to customer-specific rates (e.g. VOLVO-EXPORT gets Argon-CO2 pricing)."],
                            ["Effective Date",     "When this rate became valid. Read by Steel Price History for quarterly settlements."],
                            ["Status",             "APPROVED for live; PENDING_CONFIRMATION when Sales hasn't signed off yet."],
                        ]} />

                        <Screenshot
                            src="/manual/07-process-rates.png"
                            caption="Process Rates master: 11 cutting brackets (1-20 mm) and 7 MIG welding fillets (3-12 mm), plus Volvo-specific Argon-CO2 and Tata flat-rate welding."
                        />

                        <Callout kind="info" title="Round-up rule">
                            For welding, CostIQ picks the smallest fillet bracket <em>greater than or equal to</em>
                            the requested fillet (round-UP). Example: a 4.2 mm fillet uses the 5 mm rate.
                            Exception: Volvo Argon-CO2 joints use per-joint hardcoded rates (no round-up).
                        </Callout>

                        <Example
                            title="Example:Weld cost on a 380 m Tata tipper body"
                            inputs={[
                                ["Fillet size", "6 mm MIG fillet"],
                                ["Total weld length", "380 m"],
                                ["Matching bracket", "6 mm"],
                                ["Rate", "₹107.80 /m"],
                            ]}
                            output={[
                                ["Weld cost", "380 × 107.80 = ₹40,964"],
                                ["Appears in ZBC as", "Welding line 6 mm × 380 m"],
                            ]}
                        />

                        <p className="text-[12.5px] text-slate-600">
                            The ZBC's Cutting and Welding sub-sections call these tables at save time, and
                            customer-tagged rates override generic ones whenever the ZBC's customer matches
                            the tag.
                        </p>
                    </Section>

                    {/* ========== 5B · CONVERSION OPERATIONS ========== */}
                    <Section id="conversion-operations" title="5B · Conversion Operations" icon={Cog}>
                        <p>
                            The <strong>Conversion Operations</strong> master is the named list of standard
                            manufacturing operations (Forging, CNC Turning, Drilling, Laser Cutting…) that a
                            ZBC's Conversion Cost section picks from, instead of every ZBC re-typing the same
                            handful of operation names by hand.
                        </p>

                        <WhyMatters>
                            Before this master existed, Operation Name was a plain text box: two ZBCs for the
                            same operation could end up as "Forging", "forging", and "FORGING", making it hard
                            to roll up conversion cost by operation type across the plant. A shared master fixes
                            the spelling, and the same list can be bulk-exported/imported like every other
                            Foundation table.
                        </WhyMatters>

                        <Step n={1}>Go to <strong>Foundation Setup → Conversion Operations</strong>.</Step>
                        <Step n={2}>Click <strong>+ Add Operation</strong> and enter a Code, Operation Name, an
                        optional Machine Type hint, and a Default UOM.</Step>

                        <FieldList items={[
                            ["Code",           "Short unique code, e.g. OP-FORGE. Not shown to end users, just an internal identifier."],
                            ["Operation Name", "What appears in the ZBC's Operation Name dropdown, e.g. 'Forging'."],
                            ["Machine Type",   "Optional hint only (LASER, CNC_PRESS_BRAKE, ROBOT_WELDER, MIG_WELDER, DRILL, BANDSAW, MECH_PRESS, HYD_PRESS, PAINT_LINE, CED). Doesn't restrict which machine a ZBC can pick, it's just a label for this operation type."],
                            ["Default UOM",    "Pre-fills the ZBC row's UOM when this operation is picked (still changeable per-ZBC)."],
                        ]} />

                        <Callout kind="success" title="Doesn't replace the Machine/Rate lock">
                            Picking an operation here only sets the Operation Name (and a UOM suggestion). The
                            ZBC's Machine dropdown and its rate-lock (Section 8, Section 3 · Conversion Cost)
                            are unchanged: rate still always comes from that plant's approved Machine Rate, never
                            from this master. This master exists purely so Operation Name stops being free text.
                        </Callout>

                        <Callout kind="info" title="Anything not listed yet">
                            Pick <strong>Other (free text)</strong> in the ZBC's Operation Name dropdown for an
                            operation that isn't in this master yet, then add it here later so future ZBCs can
                            pick it from the list.
                        </Callout>

                        <FlowsInto>
                            Every <strong>ZBC → Conversion Cost</strong> row's Operation Name dropdown is sourced
                            from this master (<a className="underline" href="#zbc" onClick={(e) => { e.preventDefault(); scrollTo("zbc"); }}>Section 8</a>).
                        </FlowsInto>
                    </Section>

                    {/* ========== 6 · CUSTOMERS & NORMS ========== */}
                    <Section id="customers" title="6 · Customers & Commercial Norms" icon={Briefcase}>
                        <p>
                            A <strong>Customer</strong> record holds the OEM identity plus the commercial
                            formula variant CostIQ uses to compute the final selling price. The
                            <strong> Norm</strong> attached to each customer carries the percentages that get
                            stacked on top of the ZBC.
                        </p>

                        <WhyMatters>
                            Every OEM has its own commercial language: Volvo Export wants a 4% back-end
                            discount, Kobelco loads overhead only on the process cost, not material, and VECV
                            uses fixed-rupee overhead and profit instead of percentages. The Norm captures
                            these rules once so the RFQ engine can auto-apply them, instead of copy-pasting
                            Excel formulas per customer. Skip it and the RFQ engine falls back to a generic
                            STANDARD stack (ICC 2.5% / OH 8% / Profit 12%), which mis-prices every export or
                            fixed-fee customer and drops their discounts and volume rebates with no warning.
                        </WhyMatters>

                        <h3 className="font-display text-base font-semibold mt-4 mb-2">Add a customer</h3>
                        <Step n={1}>Foundation Setup → <strong>Customers &amp; Norms</strong>.</Step>
                        <Step n={2}>Click <strong>+ Add Customer</strong>.</Step>
                        <FieldList items={[
                            ["Customer Code",     "Short unique identifier (TATA-MOTORS, VOLVO-EXPORT, JCB-INDIA…). Referenced by ZBC's customer_tag and by every RFQ."],
                            ["Customer Name",     "Full OEM name. Prints on the RFQ commercial cover."],
                            ["Industry / Segment","CV (Commercial Vehicle), CE (Construction Equipment), FARM, EXPORT, AUTO. Drives dashboard segmentation and Variance Report filters."],
                            ["Contact Person",    "Primary buyer / SQE. Prints on RFQ header."],
                            ["Formula Variant",   "STANDARD / KOBELCO_CE / VOLVO_EXPORT / FES_LYNX / JCB_DOMESTIC / CUSTOM. This chooses which pricing math applies (see cheat sheet below, and the Custom Formula Basis note below for CUSTOM)."],
                        ]} />

                        <Screenshot
                            src="/manual/08-customers.png"
                            caption="Customers & Norms: 10 OEMs pre-seeded (Tata, Mahindra, Ashok Leyland, VECV, JCB, Kobelco, Komatsu, TAFE, Force Motors, Volvo Export). The bottom strip is the Formula Variant Cheat Sheet."
                        />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Manage norms</h3>
                        <p>Click <strong>Manage Norms</strong> on a customer row. The Norm carries:</p>
                        <FieldList items={[
                            ["ICC %",              "Inward Cost Compensation: applied on material cost. Compensates the supplier for raw-material price volatility between quote and delivery."],
                            ["Overhead %",         "Applied on process cost (or on total, per formula variant). Represents fixed-cost recovery."],
                            ["Profit %",           "Applied on the customer-specific base. Different formulas use different bases (see cheat sheet)."],
                            ["Rejection %",        "Allowance for parts rejected at customer's inward inspection. Loaded into price to keep margin whole."],
                            ["Discount %",         "Post-total discount (VOLVO_EXPORT deducts 4% here at the very end)."],
                            ["Volume Rebate ₹",    "Fixed-rupee rebate deducted after discount (Volvo-specific)."],
                            ["RM Rate Adjustment", "Per-period rate-revision adjustment applied to profit calculation."],
                            ["Freight Scope",      "ABC_COMPANY_ARRANGES (ex-factory + we deliver) or CUSTOMER_ARRANGES (ex-works). Drives the freight line in the RFQ."],
                            ["Overhead Fixed ₹",   "Only for VECV: a flat ₹ per unit instead of a %."],
                            ["Profit Fixed ₹",     "Only for VECV: a flat ₹ per unit."],
                        ]} />

                        <Callout kind="success" title="Custom formula variant for OEMs that don't fit the standard five">
                            Pick <strong>Formula Variant = Custom</strong> and a Custom Formula Basis panel
                            appears with two switches: Overhead Basis (Total Cost, meaning material plus
                            process, the default, or Process Cost Only like Kobelco) and Profit Basis (a flat
                            % on Cost + Additions, or a Split entered as <strong>Profit % on RM+BOP</strong>
                            and <strong>Profit % on Process</strong>, each falling back to the main Profit %
                            if left blank). This avoids asking engineering to add a new hardcoded variant
                            whenever an OEM's commercial structure doesn't fit the five built-in ones.
                        </Callout>

                        <Example
                            title="Example:Volvo Export RFQ price stack"
                            inputs={[
                                ["ZBC Net Cost", "₹4,850"],
                                ["ICC %", "2%"],
                                ["Overhead %", "10%"],
                                ["Profit %", "10%"],
                                ["Discount %", "4%"],
                            ]}
                            output={[
                                ["+ ICC", "₹97"],
                                ["+ Overhead", "₹485"],
                                ["+ Profit", "₹485"],
                                ["Subtotal", "₹5,917"],
                                ["− Discount 4%", "₹236.68"],
                                ["Final RFQ price", "₹5,680.32"],
                            ]}
                        />

                        <Callout kind="success" title="Saved norms are auto-applied">
                            Every RFQ created for this customer picks up the current norm. When you update a
                            norm, future RFQs use the new percentages; existing RFQs keep their snapshot so
                            approved prices don't silently drift.
                        </Callout>

                        <FlowsInto>
                            The <strong>RFQ</strong> module reads the customer's current norm to build the
                            price stack from a ZBC. The <strong>Settlement</strong> module reads the same norm
                            (plus historical steel rates) to compute the quarterly DMC delta.
                        </FlowsInto>
                    </Section>

                    {/* ========== 7 · SUPPLIERS ========== */}
                    <Section id="suppliers" title="7 · Suppliers & Steel Rates" icon={Truck}>
                        <p>
                            The <strong>Suppliers</strong> master holds every vendor CostIQ deals with, plus
                            each supplier's own steel rate by grade, kept separate from the customer-facing
                            steel grade rate in Section 2. Before this table existed, a supplier's rate and
                            ABC COMPANY's own costing rate were the same number by construction, with nowhere
                            to record that a vendor might be charging above or below the grade's market rate.
                            Splitting them means the ZBC Wizard, Supplier Compare, and the Variance Breakdown
                            drill-down (Section 8) can all show a genuine rate gap instead of assuming there
                            is none.
                        </p>

                        <Step n={1}>Go to <strong>Foundation Setup → Suppliers</strong>.</Step>
                        <Step n={2}>Click <strong>+ Add Supplier</strong> and enter the vendor's name and contact details.</Step>
                        <Step n={3}>Open a supplier's row and click <strong>Manage Steel Rates</strong> to record their
                        ₹/kg rate for one or more Steel Grades, with an effective date.</Step>

                        <FieldList items={[
                            ["Supplier Name",  "Full vendor name. Appears in every dropdown that references a supplier (Intelligent ZBC Wizard, ZBC form, Supplier Compare) instead of a free-text field."],
                            ["Steel Grade",    "Pick from the same Steel Grades master as Section 2: the supplier's rate is always tied to one of your existing grades, never a standalone number."],
                            ["Rate (₹/kg)",    "What this supplier is actually invoicing for this grade. Independent of the grade's own customer-facing rate."],
                            ["Effective Date", "When this supplier rate took effect. Older rates are kept as history, not overwritten."],
                        ]} />

                        <Callout kind="success" title="Supplier and grade are picked from the master, not typed">
                            Every place that used to ask for a supplier name or a raw RM rate as free text,
                            the Intelligent ZBC Wizard's Supplier ZBC Comparison card, the manual ZBC form's Supplier
                            field, and Supplier Workbook Compare's Supplier field, now offers a{" "}
                            <strong>dropdown</strong> sourced from this master. Picking a Supplier and Steel
                            Grade auto-fills that supplier's on-file rate, and it can still be overridden for
                            a one-off negotiated figure.
                        </Callout>

                        <Callout kind="info" title="Supplier Quotes: a separate register for whole-part quotes">
                            This section's steel rates are ₹/kg by grade, not a quote for a whole part. Sidebar →{" "}
                            <strong>Supplier Quotes</strong> is where every quote Purchase receives for a specific
                            part gets logged, whether or not it's tied to a ZBC: supplier, part, amount, date, and
                            a status (Pending/Accepted/Rejected). Linking a quote to a ZBC shows the should-cost
                            comparison automatically. Quotes older than 180 days are flagged stale, and feed the
                            Purchase Manager dashboard's "Supplier Quotes" KPI.
                        </Callout>

                        <FlowsInto>
                            The <strong>Variance Breakdown</strong> drill-down on ZBC Detail (Section 8) uses a
                            supplier's steel rate here to isolate how much of a should-cost gap is a genuine
                            <em> rate</em> difference versus a <em>weight</em> or <em>process</em> difference.
                        </FlowsInto>
                    </Section>

                    {/* ========== 7B · BOP CATALOGUE ========== */}
                    <Section id="bop-catalogue" title="7B · BOP Catalogue" icon={Package}>
                        <p>
                            The <strong>BOP Catalogue</strong> is a reusable price book for bought-out parts
                            that aren't steel and don't fit the Suppliers master's per-grade rate structure:
                            paint, hydraulic kits, fasteners, electricals, and general items. Before this
                            existed, a paint or hydraulic line on a ZBC or RFQ was a free-text lump sum typed
                            fresh every time, with no way to reuse a rate or catch it drifting between parts.
                        </p>

                        <Step n={1}>Go to <strong>Foundation Setup → BOP Catalogue</strong>.</Step>
                        <Step n={2}>Click <strong>+ Add Item</strong> (Corporate Admin only).</Step>
                        <Step n={3}>Fill in the fields:</Step>

                        <FieldList items={[
                            ["Code",     "Short unique code, e.g. PAINT-TOPCOAT-RAL, HYD-KIT-STD. Referenced by the RFQ form's catalogue picker."],
                            ["Name",     "Descriptive label shown in the picker dropdown."],
                            ["Category", "GENERIC, PAINT, HYDRAULIC, FASTENER, ELECTRICAL, or RUBBER. PAINT and HYDRAULIC are the two categories the RFQ form's Manual Cost Items section can quick-add and price directly from this catalogue."],
                            ["Rate",     "₹ per unit of measure."],
                            ["UOM",      "PER_PIECE, PER_KG, PER_SET, or PER_LITRE."],
                            ["Supplier", "Optional link to the Suppliers master, for traceability only: it doesn't affect pricing math."],
                        ]} />

                        <Callout kind="success" title="Bulk load from Excel">
                            Use the <strong>Template / Download / Import</strong> buttons at the top of the page
                            (the same import/export pattern as every other Foundation Setup master) to load or
                            refresh a whole price book at once instead of adding items one at a time.
                        </Callout>

                        <FlowsInto>
                            On the <strong>RFQ</strong> form's Manual Cost Items section, a PAINT or HYDRAULIC
                            row lets you pick an item from this catalogue instead of typing a rate, see{" "}
                            <a className="underline" href="#rfq" onClick={(e) => { e.preventDefault(); scrollTo("rfq"); }}>Section 11 · Create an RFQ</a>.
                        </FlowsInto>
                    </Section>

                    {/* ========== 8 · ZBC ========== */}
                    <Section id="zbc" title="8 · Build a ZBC (Should-Cost Analysis)" icon={Calculator}>
                        <p>
                            A <strong>ZBC</strong> ("Zero-Based Costing") is a from-scratch should-cost
                            calculation for a single part: material, conversion, cutting, welding, allowances,
                            and a comparison against the supplier's quote. ZBCs are the foundation for every RFQ.
                        </p>

                        <WhyMatters>
                            The ZBC is your negotiation weapon. It answers "What <em>should</em> this part cost us,
                            based on first-principles engineering?", regardless of what the supplier is quoting.
                            Once verified, it becomes the reference number for RFQ pricing, quarterly settlements,
                            and internal cost audits.
                        </WhyMatters>

                        <IfSkipped>
                            You can't create an RFQ without at least one linked ZBC. The Settlement module has
                            nothing to compare against. Buyers end up negotiating on gut feel: the exact
                            problem CostIQ was built to solve.
                        </IfSkipped>

                        <Screenshot
                            src="/manual/09-zbc-list.png"
                            caption="ZBC Costings list. Each row shows Part No., Customer, Plant, Should-Cost, Supplier price, Variance badge, and status. Use the top-right 'New ZBC' split-button to launch the manual form or the Intelligent wizard."
                        />

                        <Step n={1}>Modules → <strong>ZBC Costing</strong> → <strong>+ New ZBC</strong>.</Step>
                        <Step n={2}>Fill the 6 sections in order. CostIQ walks you through step by step.</Step>

                        <Callout kind="success" title="Clone from Existing ZBC">
                            On a brand-new ZBC's Part Identity section, click <strong>Clone from Existing ZBC</strong>
                            to search your saved parts by part number, name, or drawing number, and copy one's
                            dimensions, material, conversion route, cutting and welding as a starting template.
                            You still enter a new Part Number and Part Name and review every dimension before
                            saving; nothing here is copied silently. Only offered when creating a new ZBC, not
                            while editing one.
                        </Callout>

                        <Snippet label="ZBC COST STACK: how the number is built">
{`Material Cost      = (Blank kg × Grade ₹/kg)  −  (Recoverable scrap × Scrap ₹/kg)
+ Conversion Cost  = Σ (Op time hrs × Machine ₹/hr)
+ Cutting Cost     = Cut perimeter mtr × Thickness mm × Process ₹/mtr/mm
+ Welding Cost     = Σ (Weld length × Fillet-bracket ₹/mtr)
─────────────────
Base Should-Cost   ← saved as the ZBC "Net Cost"

  Later, the RFQ engine adds: ICC% + Overhead% + Profit% + Rejection% − Discount%`}
                        </Snippet>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Section 1: Part Identity</h3>
                        <FieldList items={[
                            ["Part Number / Name", "Copy verbatim from the customer's drawing. This is the primary lookup key across ZBC / RFQ / Settlement."],
                            ["Customer",           "Pick from the dropdown. Determines which norms are applied and whether customer-tagged rates (Volvo Argon-CO2, Tata flat-rate weld) kick in."],
                            ["Plant",              "Where the part will be made. Filters the machine list in Section 3 to only that plant's assets."],
                            ["RM Category",        "SHEET_STEEL / SQUARE_TUBE / ROUND_BAR / FILL_MATERIAL / BOP_PURCHASED. Drives which sub-sections show up (e.g. Cutting only appears for SHEET_STEEL / SQUARE_TUBE)."],
                            ["Supplier Quote (₹)", "What the vendor is asking per piece. Drives the coloured variance badge in Section 6 and the roll-up on the Dashboard."],
                        ]} />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Section 2: Material Cost</h3>
                        <p><strong>Two entry modes are available:</strong></p>

                        <Callout kind="success" title="Everything auto-fills, but you stay in control">
                            The moment you pick a Steel Grade and enter Sheet dimensions, CostIQ
                            auto-fills: <strong>Scrap Type</strong>, <strong>Input Weight</strong> (with a 5 mm/side
                            laser-offal allowance), <strong>Finish Weight</strong> (using seeded recoverable +
                            irrecoverable + burning-loss %), and even <strong>Cut Perimeter</strong> on Section 4.
                            A small Sparkles icon appears next to every auto-filled field; hover it
                            to see exactly how the number was derived. Type your own value any time to override,
                            then click <em>"↻ Reset to auto"</em> in the tooltip to restore the calculation.
                        </Callout>

                        <Snippet label="ENTRY MODE">
{`( ) Calculate from weight × grade rate    ← default for sheet-metal parts
( ) Enter lump amount directly             ← when RM cost comes pre-computed`}
                        </Snippet>

                        <ul className="list-disc pl-5 space-y-1.5 my-2 text-[12.5px]">
                            <li><strong>Calculate mode:</strong> pick grade + scrap, enter Blank Width × Length × Thickness, click "Calculate from dimensions" to auto-fill Input Weight. CostIQ multiplies by the grade rate and deducts scrap credit (96% recoverable).</li>
                            <li><strong>Lump mode:</strong> enter the Net Material Cost directly in ₹. Use this when the RM figure comes from an Excel SUMPRODUCT cell or a pre-priced supplier quote. Grade is optional in this mode.</li>
                        </ul>

                        <Example
                            title="Example:Material cost for a Cab Floor Bracket"
                            inputs={[
                                ["Grade", "IS2062-E250-A @ ₹62.80/kg"],
                                ["Dimensions", "450 × 320 × 6 mm"],
                                ["Density", "7.85 g/cc"],
                                ["Blank weight", "6.78 kg"],
                                ["Scrap type", "MS-HEAVY @ ₹42.80/kg"],
                                ["Recoverable %", "4%"],
                            ]}
                            output={[
                                ["Gross material", "6.78 × 62.80 = ₹425.78"],
                                ["Scrap credit", "0.271 × 42.80 = ₹11.60"],
                                ["Net material cost", "₹414.18"],
                            ]}
                        />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Section 2B: Filling Material (counterweights)</h3>
                        <p>
                            Only visible when RM Category is SHEET_STEEL. Tick "This part contains filling material"
                            to expand. Add one row per filling component (cement, hematite, water, etc.).
                            Selecting a material from the dropdown auto-fills and locks its rate from the master
                            catalogue; only picking <strong>Other (free text)</strong> leaves Rate editable. Total
                            Filling Cost flows into the RFQ as a separate RM line.
                        </p>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Section 3: Conversion Cost</h3>
                        <p>Add one operation row per process step:</p>
                        <FieldList items={[
                            ["Operation Name", "Picked from the Conversion Operations master (Section 5B), e.g. 'Forging', 'CNC Turning'; pick \"Other (free text)\" for anything not listed yet. Prints in the ZBC PDF as entered."],
                            ["Machine",        "Only machines from the ZBC's selected plant appear. Picking one auto-fills and locks its APPROVED ₹/hr from the Machine Rates table. Pick \"(manual)\" instead if the machine isn't in the master list yet; that's the only way to type a Rate by hand."],
                            ["UOM",            "PER_HOUR (cycle time based), PER_PIECE (piecework rate), PER_KG_INPUT (heat-treatment style loading)."],
                            ["Qty or Time",    "Cycle time in seconds/minutes OR piece count depending on UOM; this is the one number you always enter by hand. CostIQ converts to consistent units on save."],
                            ["Rate (₹)",       "Read-only once a Machine is picked, sourced straight from that machine's approved rate so it can't drift from a typo. Only editable when Machine is set to \"(manual)\"."],
                        ]} />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Section 4: Cutting Cost</h3>
                        <p>Applies when RM Category is SHEET_STEEL or SQUARE_TUBE. Enter <em>Cut Perimeter (mtr)</em>; CostIQ multiplies by thickness and the matching cutting rate from the Process Rates table.</p>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Section 5: Welding Cost</h3>
                        <p>Tick "Has Welding" and add one row per fillet weld. CostIQ rounds UP to the nearest fillet bracket and multiplies length × rate × qty.</p>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Section 6: Should-Cost &amp; Variance</h3>

                        <Callout kind="success" title="Tooling and CAPEX per piece">
                            Enter the one-time Tooling Cost (₹), projected Annual Volume, and Amortisation
                            Years (default 3). CostIQ computes
                            <span className="font-mono"> capex_per_piece = tooling_cost ÷ (annual_volume × years)</span>
                            {" "}and adds it as a dedicated line in the cost stack. A ₹3,00,000 die amortised
                            over 10,000 pcs/yr across 3 years works out to <strong>₹10 per piece</strong>,
                            keeping upfront capex visible in every RFQ instead of buried in overhead.
                        </Callout>

                        <Callout kind="success" title="Sub-assembly roll-up with child ZBCs">
                            Search and link any <strong>verified</strong> ZBC as a sub-assembly of this
                            part. Enter the quantity per assembly and CostIQ snapshots the child's
                            Should-Cost, computes <span className="font-mono">line = child_should_cost × qty</span>,
                            and rolls the total into the parent. Circular references are auto-rejected, which
                            enables 2- and 3-level nested BOMs, for example a Cab Sub-Assembly ZBC that includes
                            a Bracket ZBC (qty 4) and a Reinforcement Plate ZBC (qty 2).
                        </Callout>

                        <Callout kind="info" title="BOM staleness cascades to parent assemblies">
                            If you edit a child ZBC (or apply a rate-cascade recalc) and its Should-Cost changes,
                            every parent BOP_PURCHASED assembly that rolls it up as a sub-assembly is
                            automatically flagged <em>recalc-pending</em>, so nested assemblies never need
                            checking by hand. Open the parent and recompute when convenient.
                        </Callout>

                        <p>Review the live calculation. The variance badge is colour-coded:</p>
                        <Snippet label="VARIANCE BADGE">
{`GREEN  Variance < 5%      Within tolerance — accept the quote
AMBER  Variance 5-15%     Investigate cost drivers
RED    Variance > 15%     Negotiate or escalate to Dept Head
FLAG   Negative variance  Supplier quoted BELOW should-cost (verify the data)`}
                        </Snippet>

                        <Callout kind="success" title="Variance breakdown drill-down (weight, rate, process)">
                            On ZBC Detail, the three should-cost summary cards (Should-Cost, Supplier Quote,
                            Variance) are clickable: open them to see exactly why the two numbers differ, split
                            three ways. How much is a <strong>weight / geometry</strong> difference (re-pricing
                            our own numbers at the supplier's claimed weight), how much is a
                            <strong> steel-rate</strong> difference (their RM rate versus the grade rate used,
                            see <a className="underline" href="#suppliers" onClick={(e) => { e.preventDefault(); scrollTo("suppliers"); }}>Section 7 · Suppliers &amp; Steel Rates</a>),
                            and whatever's left is <strong>process</strong> (conversion, allowances, profit
                            stack). This only works when a structured supplier comparison is attached (the
                            Intelligent Wizard's Supplier ZBC Comparison card, or a matched row from Supplier Workbook
                            Compare); a plain lump ₹ supplier quote has no internal structure to decompose.
                        </Callout>

                        <Callout kind="success" title="Cross-plant cost benchmarking">
                            Open <strong>ZBC Costing → Variance Report</strong> and scroll to the
                            <strong> Cross-Plant Benchmark</strong> table. It groups every <em>VERIFIED</em> ZBC
                            by material category and plant, shows the average ₹/kg and average conversion cost
                            per plant, and flags any plant whose average deviates more than 15% from the
                            cross-plant median for that category, a quick way to spot a plant that's
                            costing a material category higher than its peers.
                        </Callout>

                        <Step n={3}>Click <strong>Save Draft</strong> to come back later. Only a <strong>Corporate
                        Admin</strong> can go straight to <strong>Mark as Verified</strong>; everyone else saves
                        as Draft and a different reviewer verifies it (see the sign-off callout below).</Step>

                        <Callout kind="warning" title="ZBC sign-off needs a second reviewer">
                            Nobody can verify their own ZBC. This is enforced by the server, not just hidden in
                            the UI. If you're Finance Pricing or a Plant Manager, click <strong>Save</strong> to
                            leave the ZBC in DRAFT, then ask a colleague (or a Corporate Admin) to open it from
                            the ZBC list and click <strong>Mark Verified</strong> on ZBC Detail. An RFQ cannot be
                            submitted for approval while any of its parts still point at an unverified ZBC; see
                            the note in <a className="underline" href="#rfq" onClick={(e) => { e.preventDefault(); scrollTo("rfq"); }}>Section 11 · Create an RFQ</a>.
                        </Callout>

                        <Callout kind="success" title="Standalone ZBC export">
                            On ZBC Detail, click <strong>Export PDF</strong> or <strong>Export Excel</strong>
                            next to Duplicate to download the full should-cost breakdown for this one part
                            (Material, Conversion, Cutting, Welding, allowances, Should-Cost, and the variance
                            against the supplier quote). This is separate from the RFQ's{" "}
                            <strong>Quotation (.pdf)</strong> export in{" "}
                            <a className="underline" href="#rfq" onClick={(e) => { e.preventDefault(); scrollTo("rfq"); }}>Section 11</a>,
                            which is a customer-facing commercial letter for a priced basket of parts; this one
                            is the internal engineering should-cost record for a single ZBC, useful for
                            supplier negotiation packs or an internal cost review before a part is ever
                            attached to an RFQ.
                        </Callout>

                        <Callout kind="warning" title="Rate Updated · Recalculation Pending → View Price Impact">
                            When a Corporate Admin updates a steel grade's rate (Section 2), every ZBC on that
                            grade is flagged <em>Recalculation Pending</em> and shows an amber banner on ZBC
                            Detail; nothing recalculates automatically. Click <strong>View Price Impact</strong>
                            right there on the banner to see the old should-cost, what it would be if
                            recalculated now, and the ₹ / % impact, then either <strong>Confirm
                            Recalculation</strong> (applies the new rate and clears the flag) or <strong>Keep Old
                            Rate</strong> (leaves the flag active so you can revisit later). Only Corporate Admin
                            and Finance Pricing can act on this.
                        </Callout>

                        <Callout kind="success" title="What-If: try a hypothetical rate before it's real">
                            Click <strong>What-If</strong> on ZBC Detail to open a sandbox: type any RM rate,
                            scrap rate, or an overall Conversion Cost Δ% (applied to every operation, cutting
                            and welding line on this part), and the should-cost recomputes live as you type.
                            Nothing is saved, so it's safe to explore "what if steel goes to ₹85/kg" before a
                            grade rate actually changes, or to sanity-check a conversion-cost negotiation ask.
                            Close the modal to discard the simulation.
                        </Callout>

                        <FlowsInto>
                            A verified ZBC becomes eligible for RFQ. The <strong>New RFQ</strong> form lets you
                            pick verified ZBCs to compose a multi-line commercial offer. The ZBC's grade &amp;
                            steel rate feed the quarterly <strong>Settlement</strong> DMC calculation.
                        </FlowsInto>
                    </Section>

                    {/* ========== 9 · Intelligent-Assisted ZBC ========== */}
                    <Section id="ai-zbc" title="9 · Intelligent-Assisted ZBC (from Drawings or Manual Input)" icon={Calculator}>
                        <p>
                            The <strong>Intelligent ZBC Wizard</strong> lets you build a ZBC in minutes: either by uploading
                            the engineering drawings (PDF) and letting <strong>Claude Sonnet 4.5</strong> extract every
                            geometry &amp; feature, or by keying in the essentials yourself. It then runs the same
                            deterministic ABC COMPANY cost engine (Material → Conversion → Allowances → Net) that
                            Section 8 uses, so the numbers are apples-to-apples with a hand-built ZBC.
                        </p>

                        <Callout kind="info" title="Where to start">
                            Sidebar → <strong>ZBC Costing</strong> → click <strong>+ Create Intelligent ZBC</strong>
                            (or the <em>Sparkles</em> icon on the ZBC list). URL:&nbsp;
                            <code>/zbc/ai-new</code>
                        </Callout>

                        <Screenshot
                            src="/manual/10-ai-zbc-wizard.png"
                            caption="Intelligent Drawing Analysis wizard. Three-step progress (Upload → Review → Save). Pick Customer + Plant first, then drop up to 10 PDF drawings. Claude Sonnet 4.5 handles the extraction."
                        />

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 1: Pick the input mode</h3>
                        <p>You have three tabs at the top of the wizard:</p>
                        <ul className="list-disc pl-5 space-y-1 text-[13px]">
                            <li><strong>Drawing Upload</strong>: drop up to <em>10 PDF drawings</em> per batch. Each becomes one "Intelligent part".</li>
                            <li><strong>Manual Entry</strong>: no drawing? Fill Part No., material and blank dimensions yourself.</li>
                            <li><strong>Catalog Search</strong>: pull an existing part from your customer's BOM catalog by part number.</li>
                        </ul>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 2: Set the customer &amp; plant</h3>
                        <Step n={1}>Pick the <strong>Customer</strong> (needed so the ICC/Profit/OH/TPT stack picks the right customer-specific norms).</Step>
                        <Step n={2}>Pick the <strong>Plant</strong> so the machine-hour rates come from that plant's rate card.</Step>
                        <Callout kind="warning" title="Don't skip this">
                            If you save a ZBC without a customer, allowances default to the ABC COMPANY standard stack and
                            the Financial Impact modal cannot compare against a supplier quote later.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 3A: Upload drawings (recommended)</h3>
                        <Step n={1}>Drag &amp; drop up to <strong>10 PDFs</strong> onto the dropzone, or click to browse.</Step>
                        <Step n={2}>Click <strong>Analyse Intelligently</strong>. Each drawing is streamed to <strong>Claude Sonnet 4.5</strong> (fallback: GPT-5.2). CostIQ extracts geometry (blank shape, dimensions, thickness, weight), material grade, GD&amp;T features, tolerance bands, threads, welds, cutouts, and infers the manufacturing route.</Step>
                        <Step n={3}>When status turns <strong>COMPLETED</strong>, the parts appear on the left rail. Click any part to review.</Step>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 3B: Manual Entry</h3>
                        <Step n={1}>Under the <strong>Manual Entry</strong> tab, enter Part Number, Part Name, Material grade.</Step>
                        <Step n={2}>Enter the blank dimensions (length &times; width &times; thickness in mm). CostIQ computes blank weight.</Step>
                        <Step n={3}>Click <strong>Create part</strong>. The part shows up on the left rail just like a drawing-analysed part.</Step>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 4: Review Part Identity &amp; Blank</h3>
                        <p>The right-hand review panel is broken into <em>cards</em>. Every card is editable via the pencil icon.</p>
                        <ul className="list-disc pl-5 space-y-1 text-[13px]">
                            <li><strong>Identity</strong>: Part Number, Part Name, Material grade. Fix any Intelligent Wizard mis-read here.</li>
                            <li><strong>Blank</strong>: Raw-material type (Plate / Sheet-metal / Round-bar / Casting), length &times; width &times; thickness, computed blank weight. If the Intelligent Wizard missed a stock-length, correct it. Everything downstream depends on this.</li>
                        </ul>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 5: Enter the Conversion Cost (Manufacturing Route)</h3>
                        <Callout kind="warning" title="Manual entry: no auto-populate">
                            CostIQ intentionally leaves the conversion-cost table empty by default. You add each row
                            explicitly, so the ZBC reflects only the operations <em>you</em> approve.
                        </Callout>
                        <Step n={1}>Click <strong>+ Add operation</strong>. Enter Operation name (e.g. Turning, Milling, Welding), pick a Machine family, key in Machine-hour rate (₹/hr) and Cycle time (seconds). The per-piece Conversion Cost auto-computes.</Step>
                        <Step n={2}>Repeat for every operation on the route.</Step>
                        <Step n={3}>(Optional) Click <strong>Load Intelligent Suggestions</strong> to pre-fill the table with the Intelligent Wizard's inferred operations. Review each row before saving.</Step>
                        <Step n={4}>Click <strong>Save conversion cost</strong> to persist the ops.</Step>

                        <Callout kind="info" title="Unit vs Corp costing view">
                            Once conversion costs are entered, a <strong>Unit vs Corp Costing View</strong> card
                            appears with three boxes, each with a small (i) tooltip icon explaining exactly how
                            it's calculated. <strong>Corp WK (Engineered)</strong> is what the drawing physically
                            demands at 100% utilisation: cycle time straight from the drawing, no real-world
                            losses. <strong>Unit WK (Realistic)</strong> divides that by the plant's actual
                            utilisation % (OEE plus setup and changeovers, default 78%, adjustable in Formula
                            Registry), giving a more realistic per-piece cost at the shop floor. <strong>Δ Unit −
                            Corp</strong> is the gap between the two, per piece and projected monthly at the
                            entered quantity. Procurement quotes typically land somewhere between the two views,
                            and that gap is the negotiating room.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 6: (Optional) Attach Supplier ZBC for side-by-side comparison</h3>
                        <p>
                            On the review panel you'll see a card labelled <strong>Supplier ZBC Comparison</strong>.
                            This lets you overlay the supplier's own ABC COMPANY-format ZBC sheet against CostIQ's calculation.
                        </p>
                        <Step n={1}>Choose one:
                            <ul className="list-disc pl-6 mt-1">
                                <li><strong>Upload supplier .xlsx</strong>: CostIQ auto-picks the sheet whose part-number matches this part.</li>
                                <li><strong>Enter manually</strong>: pick the <strong>Supplier</strong> and <strong>Steel Grade</strong> from dropdowns (both sourced from the masters in <a className="underline" href="#suppliers" onClick={(e) => { e.preventDefault(); scrollTo("suppliers"); }}>Section 7</a>, nothing typed by hand), which auto-fills their on-file RM Rate, then key in the remaining 11 headline fields (Net Material, Conversion, ICC, Tool Maint, Rejection, P&amp;F, Profit, OH, TPT, Net Price, Monthly Volume).</li>
                            </ul>
                        </Step>
                        <Step n={2}>The comparison table appears instantly: 18 metrics side-by-side with delta ₹ and Δ%. Amber highlight = 5-15% variance; red = &gt;15%.</Step>
                        <Step n={3}>The roll-up bar at the top shows <strong>Ours vs Supplier</strong>, per-piece ₹ delta, and the <strong>annualised financial impact</strong> (delta × monthly volume × 12).</Step>
                        <Callout kind="info" title="Comparing many parts at once?">
                            This is the single-part comparison, done while reviewing one Intelligent ZBC. To compare an entire
                            supplier workbook against many of your ZBCs in one upload, see <strong>Section 10 · Supplier
                            Workbook Compare</strong>.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 7: Enter your inputs and Save the ZBC</h3>
                        <Step n={1}>In the <strong>Your Inputs</strong> card at the bottom of the review panel, enter:
                            <ul className="list-disc pl-6 mt-1">
                                <li><strong>Quantity</strong>: per BOM or annual as your convention.</li>
                                <li><strong>Steel Grade</strong>: pick from the dropdown; auto-fills <strong>RM Rate (₹/kg)</strong> below from the Steel Grades master (Section 2). The field stays editable if you need a one-off override.</li>
                                <li><strong>Scrap Rate Code</strong>: pick from the dropdown; auto-fills <strong>Scrap Rate (₹/kg)</strong> below (recoverable scrap value) from the Scrap Rates master (Section 3), same override rule.</li>
                            </ul>
                        </Step>
                        <Step n={2}>Click <strong>Save as ZBC</strong>. CostIQ runs the full cost engine, applies the customer's allowance stack, and shows a toast with the final Should-Cost.</Step>
                        <Callout kind="success" title="Auto-versioning">
                            Saving the same part number a second time for the same customer does <strong>not overwrite</strong>: CostIQ appends
                            <code>-v2</code>, <code>-v3</code>… so you keep the history. All versions show up in the ZBC list.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 8: Verify from ZBC list</h3>
                        <Step n={1}>Open <strong>ZBC Costing</strong> from the sidebar. The Intelligent Wizard's saved ZBC is at the top of the list.</Step>
                        <Step n={2}>Click the part number to open ZBC Detail. If you attached a supplier ZBC, the <em>Supplier ZBC Comparison</em> block is right below the cost breakdown.</Step>
                        <Step n={3}>Tick the checkbox next to one or more ZBCs on the list and click <strong>Financial Impact</strong> to see aggregated overpayment/savings across the selection.</Step>

                        <Callout kind="info" title="Precision on Intelligent ZBCs">
                            All intermediate line items (Net Material, ICC, Profit, OH, etc.) are rounded to
                            <strong> 3 decimal places</strong> so small dimension tweaks produce visible differences.
                            The final <strong>Net Price / Should-Cost</strong> is always rounded to 2 decimals: that's
                            the number you quote.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Common gotchas</h3>
                        <ul className="list-disc pl-5 space-y-1 text-[13px]">
                            <li><strong>Blank thickness of 0</strong> → Material cost stays at ₹0. Fix in the Blank card.</li>
                            <li><strong>RM Rate blank</strong> → Save button disabled. Enter a positive value.</li>
                            <li><strong>Supplier upload fails</strong> → Ensure it's a ABC COMPANY-format .xlsx / .xlsm and one sheet per part.</li>
                            <li><strong>&gt; 10 drawings</strong> → CostIQ rejects the batch. Split into batches of 10.</li>
                        </ul>
                    </Section>

                    {/* ========== 10 · SUPPLIER WORKBOOK COMPARE ========== */}
                    <Section id="supplier-compare" title="10 · Supplier Workbook Compare" icon={FileSpreadsheet}>
                        <p>
                            Section 9's Step 6 compares <strong>one supplier value against one part</strong> while you're
                            reviewing it in the Intelligent Wizard. <strong>Supplier Workbook Compare</strong> is the bulk version:
                            upload a vendor's entire ZBC workbook (one Excel file, many part sheets) and CostIQ matches and
                            compares every part in it against your own ZBCs in a single pass. Find it in the sidebar under{" "}
                            <strong>ZBC Costing → Supplier Compare</strong>, or go directly to <code>/zbc/supplier-compare</code>.
                        </p>

                        <Callout kind="info" title="Always compares against your current numbers">
                            Every comparison recomputes your ZBCs live from today's steel rate, scrap rate, and process rates
                            before comparing: never against a stale <code>should_cost</code> that predates a rate change.
                            You don't need to open and re-save a ZBC before comparing it.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 1: Pick the customer &amp; upload</h3>
                        <Step n={1}>Pick the <strong>Customer</strong>, or leave it blank and CostIQ auto-detects it from the part numbers on the workbook's sheets. If no existing customer matches, CostIQ auto-creates one from the workbook so the comparison can proceed.</Step>
                        <Step n={2}>Drag in the supplier's <strong>.xlsx</strong> workbook (one sheet per part, plus an optional Summary sheet). CostIQ auto-detects the header row on each sheet and reads columns for Input/Finish Weight, RM/Scrap Rate, Net Material, Conversion, Sub Total, ICC, Tool Maintenance, Rejection, P&amp;F, Profit, Overhead, TPT, and Net Price.</Step>
                        <Step n={3}>Click <strong>Upload &amp; Compare</strong>. Parts are auto-matched to your ZBCs by part number.</Step>

                        <Callout kind="info" title="Auto-detecting or auto-creating the customer">
                            Leaving Customer blank does not block the upload. CostIQ scans the workbook's part
                            numbers against every customer's ZBCs; if exactly one customer matches, it's used
                            automatically. If none match, a new customer record is created on the fly from the
                            workbook so the comparison can proceed. A banner on the results page tells you
                            which happened, so the auto-created customer's norms can be cleaned up afterward
                            in <a className="underline" href="#customers" onClick={(e) => { e.preventDefault(); scrollTo("customers"); }}>Customers &amp; Norms</a>.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 2: Review the comparison</h3>
                        <Step n={1}>Each matched part shows all <strong>18 metrics side-by-side</strong> (Supplier vs DemoClient) with the ₹ delta and Δ% for each, colour-coded INFO (≤5%) / WARN (≤15%) / CRITICAL (&gt;15%).</Step>
                        <Step n={2}>Parts the workbook couldn't match to a part number show as <strong>Unmatched</strong>: use <strong>Re-match</strong> to manually point a sheet at the correct ZBC.</Step>
                        <Step n={3}>The summary bar totals the financial impact across every matched part, including <strong>annualised savings</strong> (Δ per piece × monthly volume × 12).</Step>

                        <Callout kind="info" title="The Rate-Only comparison column">
                            Alongside the normal (Full) comparison, the Financial Impact panel also shows a
                            <strong> Rate-Only</strong> column: what CostIQ's price would be if it used the
                            <em> supplier's own claimed weight</em> instead of our independently-derived one. If
                            the Rate-Only Δ is much smaller than the Full Δ, the gap is mostly in the supplier's
                            weight/geometry claim, a design conversation. If they're close, the gap is a
                            genuine rate or process-norm difference, a pricing conversation.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Step 3: Export the deliverable</h3>
                        <p>
                            Click <strong>Export .xlsx</strong> for a 3-sheet workbook you can send internally or keep on
                            file: <strong>Summary</strong> (KPIs + per-part rollup), <strong>Observations</strong> (every
                            meaningful deviation with a plain-English engineering justification and per-part/annual saving),
                            and <strong>Financial Impact</strong> (grand total annual saving).
                        </p>

                        <Callout kind="info" title="Supplier is picked from the master, not typed">
                            Before exporting, the <strong>Supplier</strong> field (used for the Excel's header)
                            is a dropdown sourced from the <a className="underline" href="#suppliers" onClick={(e) => { e.preventDefault(); scrollTo("suppliers"); }}>Suppliers master (Section 7)</a>{" "}
                            instead of free text, keeping the name consistent with what the Intelligent ZBC Wizard and
                            ZBC form use.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Common gotchas</h3>
                        <ul className="list-disc pl-5 space-y-1 text-[13px]">
                            <li><strong>Parts show as Unmatched</strong> → The sheet's part number couldn't be found in the sheet name or description. Use Re-match to map it manually.</li>
                            <li><strong>A metric column looks blank</strong> → The supplier's sheet doesn't have a column CostIQ recognises for that metric (it only reports what's actually on the sheet). This is different from it being blank on the DemoClient side, which would indicate a real problem.</li>
                            <li><strong>Whole workbook fails to parse</strong> → Confirm it's a real .xlsx (not .xls or a scanned PDF renamed to .xlsx) and follows one-sheet-per-part.</li>
                        </ul>
                    </Section>

                    {/* ========== 11 · RFQ ========== */}
                    <Section id="rfq" title="11 · Create an RFQ (Customer Price)" icon={FileText}>
                        <p>
                            An <strong>RFQ</strong> is the customer-facing price for a basket of parts. It pulls
                            costs from one or more <em>verified</em> ZBCs, applies the customer's commercial
                            formula, and produces the final selling price.
                        </p>

                        <WhyMatters>
                            The RFQ is the bridge between internal cost thinking (ZBC) and the customer
                            conversation, a quote. Because CostIQ auto-applies the customer's norm, the price
                            sent is defensible line-by-line: every rupee traces back to a foundation table
                            entry, not an Excel formula only its author understands. Skip this and quoting
                            reverts to Excel spreadsheets with no audit trail and no DH/Director inbox in the
                            loop, the exact failure mode CostIQ was built to prevent.
                        </WhyMatters>

                        <Screenshot
                            src="/manual/11-rfq-list.png"
                            caption="RFQs: Commercial Offers list. Filter by customer or status. 'New RFQ' opens the wizard that lets you pick verified ZBCs and compose the price stack."
                        />

                        <Step n={1}>Modules → <strong>RFQ Pricing</strong> → <strong>+ New RFQ</strong>.</Step>
                        <Step n={2}>Pick the customer. The formula variant (e.g. VOLVO_EXPORT) and current norm load automatically.</Step>
                        <Step n={3}>Add Parts: search the verified ZBC list and add one at a time, or click <strong>Add All (N)</strong> in the search modal to add every verified ZBC for this customer in one click.</Step>
                        <Step n={4}>Optionally add BOP items (bought-out parts) and Manual items (paint, hardware, packaging).</Step>
                        <Step n={5}>Review the cost build-up on the right panel: it updates <strong>live</strong> as you add parts/items, no need to save first.</Step>

                        <Callout kind="success" title="Live price preview">
                            The price panel on the right recomputes automatically a moment after you change anything:
                            add a part, tweak a quantity, add a BOP item. You don't need to click <strong>Save Draft</strong>
                            first to see the impact; save is only needed once you're ready to keep the RFQ.
                        </Callout>

                        <Callout kind="info" title="Paint and Hydraulic as priced catalogue items">
                            In Manual Cost Items, a <strong>PAINT</strong> or <strong>HYDRAULIC</strong> row
                            shows a catalogue picker: pick an item from the{" "}
                            <a className="underline" href="#bop-catalogue" onClick={(e) => { e.preventDefault(); scrollTo("bop-catalogue"); }}>BOP Catalogue (Section 7B)</a>{" "}
                            and CostIQ auto-fills the rate and quantity instead of a typed-in lump amount. Both
                            still add into the RFQ's Direct Material total the same way any other BOP line does.
                        </Callout>

                        <Snippet label="VOLVO_EXPORT BREAKDOWN EXAMPLE">
{`RM — Steel Shell      ₹ 9,30,376
RM — Filling           ₹ 1,42,839
BOP                    ₹ 2,37,527
Process                ₹ 48,70,690
─────────────────────────────────
ICC (2%)               ₹ 26,215
Rejection              ₹ 2
Overhead (10%)         ₹ 4,87,069
Profit (split-basis)   ₹ -90,260
─────────────────────────────────
Grand Total            ₹ 4,23,260
Discount (4%)          ₹ 16,930
Volume Rebate          ₹ 5,467
─────────────────────────────────
FINAL PRICE            ₹ 4,00,863`}
                        </Snippet>

                        <Callout kind="warning" title="Every part's ZBC must be Verified before submission">
                            If any part on the RFQ still points at a DRAFT (unverified) ZBC, <strong>Submit</strong>
                            is rejected with the list of offending part numbers. Get each one signed off from ZBC
                            Detail (see <a className="underline" href="#zbc" onClick={(e) => { e.preventDefault(); scrollTo("zbc"); }}>Section 8</a>) before resubmitting.
                        </Callout>

                        <Step n={6}>Click <strong>Submit for Approval</strong> when ready.</Step>

                        <Callout kind="success" title="What-If Simulator: steel rate and conversion cost">
                            Click <strong>What-If</strong> on RFQ Detail to try a hypothetical change without
                            saving anything. Pick a steel grade used in this RFQ and enter a new rate to see the
                            impact on final price &amp; margin (only parts on that grade move), and/or enter an
                            overall <strong>Conversion Cost Δ%</strong> to flex every part's conversion, cutting
                            and welding cost at once, e.g. to test a conversion-rate negotiation ask. Use either
                            lever alone or both together; <strong>Calculate Impact</strong> shows the simulated
                            price and margin delta side-by-side with the current one, and <strong>Undo
                            Simulation</strong> clears it. Nothing about the RFQ is ever modified by this.
                        </Callout>

                        <Callout kind="success" title="Volume-tier pricing">
                            Click <strong>Volume Tiers</strong> next to What-If to see the price at several
                            annual volumes at once (e.g. 10,000 / 50,000 / 100,000 pcs). CostIQ re-derives each
                            part's CAPEX-per-piece at that volume (tooling cost ÷ (volume × amortisation years))
                            and re-prices the whole RFQ. Nothing is saved; it's a read-only, side-by-side table
                            for negotiating volume-based pricing with the customer.
                        </Callout>

                        <Callout kind="success" title="Child-parts Excel export">
                            On any RFQ Detail page, click <strong>Child Parts (.xlsx)</strong> next to the
                            <em> Duplicate</em> button. CostIQ exports a line-item Excel with columns for
                            Part No · Qty/Assy · Annual Volume · RM · Conversion · Cutting · Welding ·
                            <strong> CAPEX/piece</strong> · <strong>Children Roll-up</strong> · Should-Cost ·
                            Extended (Qty × SC), plus a Grand Total row. Buyers use this as the appendix
                            to the commercial cover PDF.
                        </Callout>

                        <Callout kind="success" title="Quotation PDF export">
                            Click <strong>Quotation (.pdf)</strong> next to Child Parts to download a branded,
                            customer-facing commercial letter: parts covered, the full commercial build-up
                            (RM+BOP, Process, Overhead, ICC, Rejection, Profit, discounts), and the Final Price,
                            ready to send as-is.
                        </Callout>

                        <Callout kind="info" title="Negotiation history">
                            If a customer counters and the RFQ gets held/rejected, edited, and resubmitted, RFQ
                            Detail shows a <strong>Negotiation History</strong> table: one row per submission round,
                            with the final price and margin at that round and the ₹ delta versus the previous round.
                            Nothing about a past round is overwritten; each resubmission adds a new row.
                        </Callout>

                        <Callout kind="warning" title="Price impact when steel rates change after pricing">
                            If any part's ZBC should-cost has moved since this RFQ was priced, almost always a
                            steel-rate change, a yellow banner appears with <strong>View Impact</strong>: a
                            side-by-side of the stored price vs. what it would be if re-priced today, plus a
                            per-part should-cost delta table. Each changed part also shows <strong>why</strong>,
                            the reason recorded when that steel grade's rate was last updated (Section 2's
                            "Update Rate" dialog), who changed it, and the old → new rate, so a negotiator
                            never has to go dig through Steel Price History separately. Nothing changes
                            automatically; choose <strong>Re-price Now</strong> or <strong>Leave As-Is For
                            Now</strong>. This is only checked while the RFQ is still in preparation
                            (Draft/Held/Pending): a finally decided RFQ's price is a historical record and
                            isn't re-checked.
                        </Callout>

                        <Callout kind="info" title="Recording the customer outcome">
                            Once an RFQ reaches Director-approved / Approved status, a <strong>Customer
                            Outcome</strong> panel appears on RFQ Detail. Recording Won, Lost, or Partial isn't
                            required, but it's the only place in CostIQ that captures what actually happened
                            after approval. The data feeds future win-rate reporting by formula variant and
                            margin band.
                        </Callout>

                        <p className="text-[12.5px] text-slate-600">
                            An approved RFQ locks its price snapshot, and downstream Settlements for that
                            customer reconcile against these locked prices when steel rates change.
                        </p>
                    </Section>

                    {/* ========== 12 · APPROVAL ========== */}
                    <Section id="approval" title="12 · Approval Workflow" icon={ShieldCheck}>
                        <p>
                            RFQs route through a multi-tier approval based on margin and customer segment.
                            CostIQ auto-determines the next approver; you'll see an inbox in your sidebar
                            named <strong>Pending My Approval</strong>. This guarantees no price leaves the
                            company without the right eyes on it: the system computes margin automatically
                            and picks the correct approver tier, so Finance never accidentally ships a
                            below-threshold quote.
                        </p>

                        <Snippet label="APPROVAL CHAIN">
{`Finance Pricing  →  creates RFQ, submits for approval
                       │
                       ▼
Dept Head        →  required when margin < 12% (configurable threshold)
                       │
                       ▼
Director         →  required for all CE-export and high-value RFQs

Each approver can:  APPROVE  · REJECT (with reason)  · REQUEST CHANGES`}
                        </Snippet>

                        <Step n={1}>Open <strong>Modules → Pending My Approval</strong>.</Step>
                        <Step n={2}>Click any RFQ to see the full breakdown.</Step>
                        <Step n={3}>Choose Approve, Reject, or Request Changes. A comment is required for the last two.</Step>

                        <Callout kind="info" title="Audit trail">
                            Every state change is logged with user, timestamp, and reason. The audit log is
                            visible to Corporate Admin and Director under Administration → Audit Log.
                        </Callout>
                    </Section>

                    {/* ========== 13 · SETTLEMENT ========== */}
                    <Section id="settlement" title="13 · Settlement (Quarterly Steel-Price Revision)" icon={Wallet}>
                        <p>
                            <strong>Settlement</strong> is the quarterly reconciliation of steel-price movements
                            with each OEM. For every steel grade, CostIQ compares the rate you're proposing
                            against the last-agreed rate and the SIAM benchmark, computes the resulting DMC
                            (Differential Material Cost) using actual steel consumption from that quarter's sale
                            register, adds any weight-gap recovery, and gives an ACCEPT / COUNTER / REVIEW
                            advisory per grade, so Finance walks into the negotiation with a defensible number
                            instead of an opinion. When SAIL or JSW revises a grade by ₹3/kg, that swing
                            multiplied across the quarter's volume can be worth crores, and Settlement is where
                            it gets recovered from (or refunded to) the OEM.
                        </p>

                        <Screenshot
                            src="/manual/12-settlement.png"
                            caption="Settlements: Quarterly Negotiations screen. 'SIAM History' shows the last 8 quarters of steel-index movement. 'New Settlement' launches the 6-tab wizard."
                        />

                        <Step n={1}>Modules → <strong>Settlement</strong> → <strong>+ New Settlement</strong>. The
                        form is a 6-tab wizard: Setup, Rate Matrix, Sale Register, Weight Gap, DMC Preview, Synopsis.</Step>
                        <Step n={2}><strong>1 · Setup</strong>: pick the customer, Settlement Type (Raw Material /
                        BOP / Combined), the period (From/To), and this quarter's SIAM reference rates
                        (HR ≤ 10mm, HR &gt; 10mm, CR Sheet, Scrap).</Step>
                        <Step n={3}><strong>2 · Rate Matrix</strong>: add each steel grade under negotiation. Last
                        Agreed auto-fills from the grade master; enter the rate you're Proposing and the SIAM
                        benchmark. Movement and vs-SIAM compute live as you type.</Step>
                        <Step n={4}><strong>3 · Sale Register</strong>: upload a CSV (Part No · Description · Qty ·
                        Customer Weight) or add rows manually. On save, each part is matched to its ZBC's BOM
                        weight, this is what drives both the DMC steel-kg figure and the weight-gap recovery.</Step>
                        <Step n={5}><strong>4 · Weight Gap</strong> and <strong>5 · DMC Preview</strong> are read-only
                        previews (the DMC Preview tab is explicitly rough, a local estimate; the real ₹ figures need
                        the server-side BOM match and are only final once saved).</Step>
                        <Step n={6}>Click <strong>Save Draft</strong> to keep working, or <strong>Save &amp;
                        Submit</strong> to send it straight to Director Review.</Step>

                        <Callout kind="info" title="ACCEPT / COUNTER / REVIEW advisory">
                            Once saved, each Rate Matrix row on Settlement Detail carries an Intelligent Advisor recommendation:
                            <strong> ACCEPT</strong> (proposed is at or below SIAM, a fair market rate),
                            <strong> COUNTER</strong> (proposed is above SIAM, counter at the SIAM rate), or
                            <strong> REVIEW</strong> (no SIAM rate entered, or the movement itself looks negative
                            and needs a manual check against current invoices). A strategy summary line rolls
                            all grades up into one sentence for the negotiation call.
                        </Callout>

                        <Callout kind="success" title="Approve &amp; Lock, Hold, or Withdraw">
                            On Settlement Detail, a Corporate Admin or Finance Pricing user can <strong>Withdraw</strong>
                            a submitted settlement back to Draft to fix something before Director review. The
                            Director then either <strong>Approve &amp; Lock</strong> (a comment is required; this is
                            permanent per Business Rule 5, no further edits or rollback) or <strong>Hold</strong>
                            (also with a comment, sending it back for changes without locking anything).
                        </Callout>

                        <Callout kind="success" title="What locking does">
                            Locked settlements feed the next quarter's RFQ pricing automatically and appear in
                            the Steel Price History chart. They cannot be edited: only superseded by the next
                            quarter's settlement.
                        </Callout>

                        <Callout kind="success" title="Claim Breakup drill-down">
                            On Settlement Detail, the <strong>Total Claim</strong>, <strong>DMC (Full
                            Period)</strong> and <strong>Weight-Gap Recovery</strong> cards are clickable: open
                            them to see exactly what's driving the claim, split into its two components. The
                            steel-rate driver breaks down grade by grade (rate movement × steel kg used), and
                            the weight driver breaks down part by part (only rows with a recoverable gap are
                            shown). Useful for defending the total claim line-by-line in front of the customer
                            instead of quoting one lump number.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-4 mb-2">Exports</h3>
                        <p>
                            From any settlement detail page, use <strong>Export PDF</strong> or
                            <strong> Export Excel</strong> to send the result to the customer. The PDF carries
                            ABC COMPANY's letterhead and the Director's e-signature line.
                        </p>
                    </Section>

                    {/* ========== 14 · REPORTS ========== */}
                    <Section id="reports" title="14 · Reports" icon={ClipboardList}>
                        <p>
                            <strong>Reports</strong> is a parameter-driven view of the ZBC and RFQ pipeline,
                            built to answer "how many ZBCs did we receive, clear, or have pending, and where
                            is each RFQ stuck in approval" without exporting anything to Excel first. Before
                            this existed, answering "how many RFQs are pending at Director level this month"
                            meant opening the RFQ list and counting by eye; Reports turns that into a
                            filtered, always-current view, the same numbers worth having in front of you
                            before a review with an AVP or Director.
                        </p>

                        <Step n={1}>Sidebar → <strong>Reports</strong> (under Modules). URL: <code>/reports</code>.</Step>
                        <Step n={2}>Set the filters at the top: <strong>Date range</strong>, <strong>Plant</strong>,
                        <strong> Customer</strong>, any combination, or leave all blank for the full picture.</Step>
                        <Step n={3}>Toggle <strong>Corporate view</strong>, <strong>By Unit (plant)</strong>, or{" "}
                        <strong>By Customer</strong> to switch between one rolled-up number, a per-plant
                        breakdown, and a per-customer breakdown.</Step>

                        <Callout kind="success" title="Exporting to Excel">
                            Click <strong>Export (.xlsx)</strong> (top-right) to download a 4-sheet workbook:
                            <strong> ZBC Pipeline</strong>, <strong>RFQ Approval Pipeline</strong>,{" "}
                            <strong>By Customer</strong>, and <strong>Settlement Claims</strong>, with corporate
                            totals and the full by-plant/by-customer breakdown on each. It respects whatever
                            filters are currently set, so the download always matches what's on screen: hand this
                            to an AVP/Director review instead of retyping the numbers into a slide.
                        </Callout>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">ZBC Pipeline</h3>
                        <p>Shows <strong>Received</strong> (total in the selected window), <strong>Cleared</strong>
                        (Verified), and <strong>Pending</strong> (still Draft). Received always equals
                        Cleared + Pending.</p>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">RFQ Approval Pipeline</h3>
                        <p>
                            Shows every RFQ status side-by-side: Draft, Pending DH, Pending Director, Held,
                            Approved, Director Approved, Rejected, Withdrawn: plus a count of{" "}
                            <strong>Approved in range</strong> for the selected date window. This is what
                            answers "how many are pending at whose level right now."
                        </p>

                        <Callout kind="info" title="How the Plant filter works for RFQs">
                            An RFQ itself doesn't carry a single plant: it's derived from the plant of its
                            first part's ZBC. This is a reasonable proxy for almost every RFQ, but if a single
                            RFQ genuinely spans parts made at different plants, its "By Unit" row reflects only
                            that first part's plant.
                        </Callout>

                        <p className="text-[12.5px] text-slate-600">
                            Use this alongside <a className="underline" href="#rfq" onClick={(e) => { e.preventDefault(); scrollTo("rfq"); }}>Section 11 · Create an RFQ</a>'s
                            Negotiation History and Price Impact views when preparing a management review:
                            Reports gives the aggregate count, those give the per-RFQ detail.
                        </p>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Settlement Claims</h3>
                        <p>
                            Shows <strong>Director-approved (locked)</strong> settlements only, the same
                            "realized claim" scope the Director dashboard's quarterly sum already uses, just
                            made filterable by date range and customer instead of fixed to the current quarter:
                            count, DMC (Full Period), Weight-Gap Recovery, and Total Claim. Settlement has no
                            plant field, so the Plant filter doesn't scope this card.
                        </p>

                        <h3 className="font-display text-base font-semibold mt-5 mb-2">Trend</h3>
                        <p>
                            Two line charts, ZBC Received vs. Cleared and RFQ Received vs. Approved, bucketed by
                            month across whatever's in the current scope. Unlike the cards above, the trend isn't
                            limited to the selected date window: it plots by each record's own month so the shape
                            of history stays visible even with no date range set. Needs at least two months of
                            data to draw a line.
                        </p>

                        <Callout kind="info" title="Quality Signals: already built elsewhere, surfaced here">
                            <strong>RFQ Win Rate</strong>, <strong>Variance Flags</strong> (RED + FLAG badge
                            count), and <strong>Anomalies Flagged</strong> are three signals that already exist
                            as their own features (<a className="underline" href="#rfq" onClick={(e) => { e.preventDefault(); scrollTo("rfq"); }}>Section 11</a>'s
                            customer outcomes, the Variance Report, and the Dashboard's Anomaly Detector). This
                            card is a read-only summary of all three in one place; it isn't scoped by the date
                            range/plant/customer filters above since those source features aren't either. Click
                            any tile to jump to the full, filterable view.
                        </Callout>
                    </Section>

                    {/* ========== 15 · ROLES ========== */}
                    <Section id="roles" title="15 · Roles & Permissions" icon={UsersIcon}>
                        <p>CostIQ has six roles. Each role sees only the menus relevant to its work.</p>
                        <FieldList items={[
                            ["Corporate Admin",  "Full access: foundation setup, user management, settings, audit log, all modules."],
                            ["Finance Pricing",  "Creates ZBCs and RFQs, runs Variance Reports."],
                            ["Dept Head",        "Approves RFQs below the Director threshold. Reads ZBC and RFQ data."],
                            ["Plant Manager",    "Maintains Machines and Machine Rates for their plant."],
                            ["Purchase Manager", "Manages Supplier Quotes."],
                            ["Director",         "Final approver on RFQs and Settlement sign-offs. Reads audit log."],
                        ]} />

                        <h3 className="font-display text-base font-semibold mt-4 mb-2">Add a new user</h3>
                        <Step n={1}>Administration → <strong>Users &amp; Roles</strong>.</Step>
                        <Step n={2}>Click <strong>+ Add User</strong>: enter name, email, role, plant (if applicable).</Step>
                        <Step n={3}>The user receives a one-time password by email. They change it on first login.</Step>
                    </Section>

                    {/* ========== 16 · TIPS ========== */}
                    <Section id="tips" title="16 · Tips & Best Practices" icon={BookOpen}>
                        <ul className="list-disc pl-5 space-y-2">
                            <li><strong>Use the global search</strong> (top of the page, ⌘K shortcut) to jump to any part, RFQ, customer, or settlement.</li>
                            <li><strong>Save Drafts often</strong>. You can resume any unfinished ZBC or RFQ from the list page.</li>
                            <li><strong>Update rates centrally</strong>: never override a rate inside an individual ZBC. Updates flow to all future ZBCs automatically.</li>
                            <li><strong>Customer-tag what's customer-specific</strong>. If a customer needs their own steel grade, scrap rate, or welding rate, tag it instead of duplicating master data.</li>
                            <li><strong>Watch the variance badge</strong>. RED variances usually mean a missing operation, wrong grade, or stale supplier quote: investigate before submitting an RFQ.</li>
                            <li><strong>Lock settlements promptly</strong>. Delayed sign-off can stall the next quarter's RFQ pricing.</li>
                            <li><strong>Use TATA Demo View</strong> for the legacy 2018 reference pack when training new finance staff.</li>
                            <li><strong>Audit log is your friend</strong>: when in doubt about who changed what, check Administration → Audit Log.</li>
                        </ul>

                        <Callout kind="info" title="Still stuck?">
                            Reach out to your Corporate Admin (Demo User) for access issues, or to your
                            Dept Head for workflow guidance. For platform bugs, contact Kairoo Technologies.
                        </Callout>
                    </Section>

                </main>
            </div>
        </div>
    );
}
